import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import layoutService from '../services/layoutService';

const MAX_FAVORITES = 50;
const FAVORITES_EVENT = 'favorite-pages-changed';

const normalize = (entry) => {
    if (typeof entry === 'string') {
        return { path: entry, title: null, bold: false, color: null };
    }
    return {
        path: entry.path,
        title: entry.title ?? null,
        bold: entry.bold === true,
        color: entry.color ?? null,
    };
};

const normalizeList = (list) => (Array.isArray(list) ? list.filter(Boolean).map(normalize) : []);

const persist = (next) => {
    layoutService.saveFavorites(next);
    setTimeout(() => window.dispatchEvent(new CustomEvent(FAVORITES_EVENT, { detail: next })), 0);
};

const useFavoritePages = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const [favorites, setFavorites] = useState(() => normalizeList(layoutService.getCachedFavorites()));
    const [loading, setLoading] = useState(true);
    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (!isAuthenticated || initialFetchDone.current) {
            setLoading(false);
            return;
        }

        const syncFromServer = async () => {
            try {
                const serverFavorites = await layoutService.getFavorites();
                if (serverFavorites.length > 0) {
                    setFavorites(normalizeList(serverFavorites));
                }
            } catch {} finally {
                setLoading(false);
                initialFetchDone.current = true;
            }
        };

        syncFromServer();
    }, [isAuthenticated]);

    useEffect(() => {
        const onChange = (e) => setFavorites(normalizeList(e.detail));
        window.addEventListener(FAVORITES_EVENT, onChange);
        return () => window.removeEventListener(FAVORITES_EVENT, onChange);
    }, []);

    const isFavorite = useCallback((path) => {
        return favorites.some(f => f.path === path);
    }, [favorites]);

    const toggleFavorite = useCallback((path, title) => {
        setFavorites(prev => {
            const exists = prev.some(f => f.path === path);
            let next;
            if (exists) {
                next = prev.filter(f => f.path !== path);
            } else {
                if (prev.length >= MAX_FAVORITES) return prev;
                next = [...prev, { path, title: title ?? null, bold: false, color: null }];
            }
            persist(next);
            return next;
        });
    }, []);

    const removeFavorite = useCallback((path) => {
        setFavorites(prev => {
            const next = prev.filter(f => f.path !== path);
            persist(next);
            return next;
        });
    }, []);

    const updateLabel = useCallback((path, label) => {
        setFavorites(prev => {
            const trimmed = (label ?? '').trim();
            const next = prev.map(f => f.path === path ? { ...f, title: trimmed || null } : f);
            persist(next);
            return next;
        });
    }, []);

    const toggleBold = useCallback((path) => {
        setFavorites(prev => {
            const next = prev.map(f => f.path === path ? { ...f, bold: !f.bold } : f);
            persist(next);
            return next;
        });
    }, []);

    const setColor = useCallback((path, color) => {
        setFavorites(prev => {
            const next = prev.map(f => f.path === path ? { ...f, color: color || null } : f);
            persist(next);
            return next;
        });
    }, []);

    const reorder = useCallback((fromIndex, toIndex) => {
        setFavorites(prev => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return prev;
            if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            persist(next);
            return next;
        });
    }, []);

    return {
        favorites,
        isFavorite,
        toggleFavorite,
        removeFavorite,
        updateLabel,
        toggleBold,
        setColor,
        reorder,
        loading,
        max: MAX_FAVORITES,
    };
};

export default useFavoritePages;
